import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil, Loader2, Star, Crown, Zap, Rocket, Sparkles, X as XIcon } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import TranslateButton from './TranslateButton';

const ICONS = { zap: Zap, crown: Crown, star: Star, rocket: Rocket, sparkles: Sparkles };

const DEFAULT_PRICE_COLOR = '#F27600';

const emptyPlan = {
  nameAr: '', nameEn: '', taglineAr: '', taglineEn: '',
  icon: 'zap', price: 0, original_price: 0, price_color: '', annual_price: 0, period: 'monthly',
  features: [{ ok: true, ar: '', en: '' }],
  popular: false, badgeAr: '', badgeEn: '', order: 0,
};

const PlansManager = () => {
  const { lang, isRTL } = useLang();
  const { toast } = useToast();
  const L = (ar, en) => tx(lang, ar, en);
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPlan);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/plans');
      setPlans(data || []);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing('new'); setForm(emptyPlan); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      nameAr: p.nameAr || '', nameEn: p.nameEn || '',
      taglineAr: p.taglineAr || '', taglineEn: p.taglineEn || '',
      icon: p.icon || 'zap',
      price: p.price || 0, original_price: p.original_price || 0, price_color: p.price_color || '', annual_price: p.annual_price || 0,
      period: p.period || 'monthly',
      features: p.features?.length ? p.features : [{ ok: true, ar: '', en: '' }],
      popular: !!p.popular,
      badgeAr: p.badgeAr || '', badgeEn: p.badgeEn || '',
      order: p.order || 0,
    });
  };

  const close = () => { setEditing(null); setForm(emptyPlan); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing === 'new') {
        const { data } = await api.post('/plans', form);
        setPlans([...plans, data].sort((a, b) => a.order - b.order));
        toast({ title: L('تم إنشاء الباقة', 'Plan created') });
      } else {
        const { data } = await api.patch(`/plans/${editing}`, form);
        setPlans(plans.map(p => p.id === data.id ? data : p));
        toast({ title: L('تم التعديل', 'Updated') });
      }
      close();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(L('هل أنت متأكد من الحذف؟', 'Are you sure?'))) return;
    try {
      await api.delete(`/plans/${id}`);
      setPlans(plans.filter(p => p.id !== id));
      toast({ title: L('تم الحذف', 'Deleted') });
    } catch {}
  };

  const addFeature = () => setForm({ ...form, features: [...form.features, { ok: true, ar: '', en: '' }] });
  const removeFeature = (i) => setForm({ ...form, features: form.features.filter((_, idx) => idx !== i) });
  const updateFeature = (i, key, val) => setForm({ ...form, features: form.features.map((f, idx) => idx === i ? { ...f, [key]: val } : f) });

  // Set value by dotted path (supports "features.0.ar", "nameAr", etc.)
  const setByPath = (path, value) => {
    setForm(prev => {
      const parts = path.split('.');
      const next = Array.isArray(prev) ? [...prev] : { ...prev };
      let cur = next;
      for (let i = 0; i < parts.length - 1; i += 1) {
        const key = parts[i];
        const idx = /^\d+$/.test(key) ? parseInt(key, 10) : key;
        const child = cur[idx];
        const cloned = Array.isArray(child) ? [...child] : { ...child };
        cur[idx] = cloned;
        cur = cloned;
      }
      const last = parts[parts.length - 1];
      cur[/^\d+$/.test(last) ? parseInt(last, 10) : last] = value;
      return next;
    });
  };

  // Build per-direction field maps including dynamic feature rows
  const buildTranslateFields = (dir) => {
    const base = dir === 'ar2en'
      ? [
          { source: 'nameAr', target: 'nameEn' },
          { source: 'taglineAr', target: 'taglineEn' },
          { source: 'badgeAr', target: 'badgeEn' },
        ]
      : [
          { source: 'nameEn', target: 'nameAr' },
          { source: 'taglineEn', target: 'taglineAr' },
          { source: 'badgeEn', target: 'badgeAr' },
        ];
    (form.features || []).forEach((_, i) => {
      base.push(dir === 'ar2en'
        ? { source: `features.${i}.ar`, target: `features.${i}.en` }
        : { source: `features.${i}.en`, target: `features.${i}.ar` });
    });
    return base;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-slate-900 font-bold text-lg">{L('إدارة باقات الاشتراك', 'Manage Subscription Plans')}</h3>
        <Button onClick={openNew} data-testid="add-plan-btn" className="btn-primary-blue border-0 h-10 px-4">
          <Plus className="w-4 h-4 me-1" />{L('إضافة باقة', 'Add Plan')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-10">
            {L('لا توجد باقات بعد. أضف الباقة الأولى!', 'No plans yet. Add your first plan!')}
          </div>
        )}
        {plans.map(p => {
          const Ic = ICONS[p.icon] || Zap;
          return (
            <div key={p.id} className={`rounded-xl border ${p.popular ? 'border-amber-500/60 bg-gradient-to-br from-amber-500/5 to-white' : 'border-slate-200 bg-white'} p-5`}>
              <div className={`flex items-center gap-3 mb-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${p.popular ? 'bg-amber-500/20' : 'bg-blue-50'}`}>
                  <Ic className={`w-5 h-5 ${p.popular ? 'text-amber-600' : 'text-blue-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-900 font-bold truncate">{lang === 'ar' ? p.nameAr : p.nameEn}</div>
                  <div className="text-slate-500 text-xs truncate">{lang === 'ar' ? p.taglineAr : p.taglineEn}</div>
                </div>
                {p.popular && <Star className="w-4 h-4 text-amber-600 fill-amber-400" />}
              </div>
              <div className="text-2xl font-extrabold text-slate-900 mb-1" dir="ltr">${p.price} <span className="text-sm text-slate-500">USD</span></div>
              <div className="text-xs text-slate-500 mb-3">{p.period} · {p.features?.filter(f => f.ok).length || 0} {L('ميزة', 'features')}</div>
              <div className="flex gap-2">
                <Button onClick={() => openEdit(p)} variant="outline" className="flex-1 border-blue-200 text-blue-700 hover:bg-slate-100 h-9"><Pencil className="w-4 h-4 me-1" />{L('تعديل', 'Edit')}</Button>
                <Button onClick={() => remove(p.id)} variant="outline" className="flex-1 border-red-500/40 text-red-600 hover:bg-red-500/10 h-9"><Trash2 className="w-4 h-4 me-1" />{L('حذف', 'Delete')}</Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 text-xl">
              {editing === 'new' ? L('إضافة باقة جديدة', 'Add New Plan') : L('تعديل الباقة', 'Edit Plan')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input data-testid="plan-name-ar" required value={form.nameAr} onChange={e => setForm({ ...form, nameAr: e.target.value })} placeholder={L('اسم الباقة بالعربية', 'Name Arabic')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
              <Input data-testid="plan-name-en" value={form.nameEn} onChange={e => setForm({ ...form, nameEn: e.target.value })} placeholder={L('اسم الباقة بالإنجليزية', 'Name English')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" dir="ltr" />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Input data-testid="plan-tagline-ar" value={form.taglineAr} onChange={e => setForm({ ...form, taglineAr: e.target.value })} placeholder={L('الوصف الفرعي بالعربية', 'Tagline Arabic')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
              <Input data-testid="plan-tagline-en" value={form.taglineEn} onChange={e => setForm({ ...form, taglineEn: e.target.value })} placeholder={L('الوصف الفرعي بالإنجليزية', 'Tagline English')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" dir="ltr" />
            </div>

            <div className="flex flex-wrap gap-2">
              <TranslateButton
                sourceLang="ar"
                targetLang="en"
                getValues={() => form}
                setValue={setByPath}
                fields={buildTranslateFields('ar2en')}
                testId="plan-translate-ar-en"
              />
              <TranslateButton
                sourceLang="en"
                targetLang="ar"
                getValues={() => form}
                setValue={setByPath}
                fields={buildTranslateFields('en2ar')}
                testId="plan-translate-en-ar"
              />
            </div>

            <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div>
                <label className="text-slate-600 text-xs mb-1.5 block">{L('السعر (دولار)', 'Price (USD)')}</label>
                <Input type="number" min={0} step="0.01" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
              </div>
              <div>
                <label className="text-slate-600 text-xs mb-1.5 block">{L('السعر قبل الخصم (اختياري)', 'Price before discount (optional)')}</label>
                <Input data-testid="plan-original-price-input" type="number" min={0} step="0.01" value={form.original_price} onChange={e => setForm({ ...form, original_price: parseFloat(e.target.value) || 0 })} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
              </div>
              <div>
                <label className="text-slate-600 text-xs mb-1.5 block">{L('لون السعر', 'Price color')}</label>
                <div className="flex items-center gap-2 h-11 bg-slate-50 border border-slate-200 rounded-md px-2">
                  <input data-testid="plan-price-color-input" type="color" value={form.price_color || DEFAULT_PRICE_COLOR} onChange={e => setForm({ ...form, price_color: e.target.value })} className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent p-0" />
                  <span data-testid="plan-price-color-value" dir="ltr" className="text-slate-700 text-sm font-mono flex-1">{(form.price_color || DEFAULT_PRICE_COLOR).toUpperCase()}</span>
                  {form.price_color && <button type="button" data-testid="plan-price-color-reset" onClick={() => setForm({ ...form, price_color: '' })} className="text-xs text-blue-600 hover:underline">{L('افتراضي', 'Default')}</button>}
                </div>
              </div>
              <div>
                <label className="text-slate-600 text-xs mb-1.5 block">{L('السعر السنوي', 'Annual Price')}</label>
                <Input type="number" min={0} step="0.01" value={form.annual_price} onChange={e => setForm({ ...form, annual_price: parseFloat(e.target.value) || 0 })} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
              </div>
              <div>
                <label className="text-slate-600 text-xs mb-1.5 block">{L('الفترة', 'Period')}</label>
                <select value={form.period} onChange={e => setForm({ ...form, period: e.target.value })} className="w-full bg-slate-50 border border-slate-200 text-slate-900 h-11 rounded-md px-3">
                  <option value="weekly">{L('أسبوعياً', 'Weekly')}</option>
                  <option value="monthly">{L('شهرياً', 'Monthly')}</option>
                  <option value="quarterly">{L('كل 3 أشهر', 'Every 3 months')}</option>
                  <option value="biannual">{L('كل 6 أشهر', 'Every 6 months')}</option>
                  <option value="yearly">{L('سنوياً', 'Yearly')}</option>
                </select>
              </div>
              <div>
                <label className="text-slate-600 text-xs mb-1.5 block">{L('الترتيب', 'Order')}</label>
                <Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-slate-600 text-xs mb-1.5 block">{L('الأيقونة', 'Icon')}</label>
                <select value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} className="w-full bg-slate-50 border border-slate-200 text-slate-900 h-11 rounded-md px-3">
                  <option value="zap">⚡ Zap</option>
                  <option value="crown">👑 Crown</option>
                  <option value="star">⭐ Star</option>
                  <option value="rocket">🚀 Rocket</option>
                  <option value="sparkles">✨ Sparkles</option>
                </select>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={form.popular} onCheckedChange={(v) => setForm({ ...form, popular: v })} />
                <span className="text-slate-700 text-sm">{L('باقة مميزة (الأكثر شعبية)', 'Mark as popular')}</span>
              </div>
            </div>

            {form.popular && (
              <div className="grid sm:grid-cols-2 gap-3">
                <Input value={form.badgeAr} onChange={e => setForm({ ...form, badgeAr: e.target.value })} placeholder={L('نص الشارة بالعربية (مثال: وفّر 67%)', 'Badge text Arabic')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" />
                <Input value={form.badgeEn} onChange={e => setForm({ ...form, badgeEn: e.target.value })} placeholder={L('نص الشارة بالإنجليزية', 'Badge text English')} className="bg-slate-50 border-slate-200 text-slate-900 h-11" dir="ltr" />
              </div>
            )}

            {/* Features list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-slate-600 text-sm">{L('المميزات', 'Features')}</label>
                <Button type="button" onClick={addFeature} variant="outline" className="border-blue-200 text-blue-700 hover:bg-slate-100 h-8 text-xs"><Plus className="w-3 h-3 me-1" />{L('إضافة ميزة', 'Add feature')}</Button>
              </div>
              <div className="space-y-2">
                {form.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                    <Switch checked={f.ok} onCheckedChange={(v) => updateFeature(i, 'ok', v)} />
                    <Input data-testid={`plan-feature-${i}-ar`} value={f.ar} onChange={e => updateFeature(i, 'ar', e.target.value)} placeholder={L('بالعربية', 'Arabic')} className="bg-white border-slate-200 text-slate-900 h-9 text-sm" />
                    <Input data-testid={`plan-feature-${i}-en`} value={f.en} onChange={e => updateFeature(i, 'en', e.target.value)} placeholder={L('بالإنجليزية', 'English')} className="bg-white border-slate-200 text-slate-900 h-9 text-sm" dir="ltr" />
                    <button type="button" onClick={() => removeFeature(i)} className="w-9 h-9 rounded-md text-red-600 hover:bg-red-500/10 flex items-center justify-center shrink-0">
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={busy} className="btn-primary-blue border-0 h-11 px-7 font-semibold">
                {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
                {editing === 'new' ? L('إنشاء', 'Create') : L('حفظ', 'Save')}
              </Button>
              <Button type="button" onClick={close} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 h-11">
                {L('إلغاء', 'Cancel')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlansManager;
